import Icon from 'components/Icon'
import * as React from 'react'
import cx from 'classnames'
import styles from './Avatar.m.sass'
import defaultAvatarUrl from './ic_default_avatar.svg'
import { isNotNil } from 'utils/object-util'
import { observer } from 'mobx-react'
import PresenceIndicator from 'components/PresenceIndicator'
import { match } from 'utils/pattern-match-util'

/**
 * The subject who's avatar is being displayed.
 */
export interface AvatarSubject {
  avatar?: string | null
  avatarFallback?: string | null
  displayName?: string | null
}

/**
 * Avatar props.
 */
export interface AvatarProps extends React.HTMLAttributes<HTMLSpanElement> {
  greyscale?: boolean
  subject: AvatarSubject | Array<AvatarSubject> | null | undefined
  size?: number
  square?: boolean

  /**
   * By default, when setting `bordered` to `true`, the `size` you specify
   * will be preserved. The image itself will shrink to accommodate the border
   * in a manner that satisfies: `(avatar size) + (2 * border-size) = size`.
   */
  bordered?: boolean
  /**
   * If set, does not attempt to fit the image.
   */
  raw?: boolean
  /**
   * If set, renders an badge circle as an adornment.
   */
  badge?: Badge
  /**
   * Useful when using custom svg images which need to fill the container with a
   * different color.
   */
  backgroundColor?: string
  /**
   * Whether to show an exclamation instead of the avatar.
   */
  exclaim?: boolean
  dataTest?: string
}

/**
 * Badge
 */
export const enum Badge {
  None,
  Online,
  Offline,
  CheckMark,
  ErrorMark,
}

/**
 * Avatar component.
 */
export default observer(function Avatar({
  greyscale,
  size,
  subject,
  square = false,
  bordered = false,
  raw = false,
  badge = Badge.None,
  exclaim = false,
  dataTest,
  backgroundColor,
}: AvatarProps) {
  // In case the value is observable.
  const subjects = mapSubjectsToImageProps(subject)
  return (
    <span
      className={styles.root}
      style={{
        // The styles use the font-size for scaling.
        fontSize: size,
      }}
      data-test={dataTest}
    >
      <span
        className={cx(square ? styles.square : styles.round, raw && styles.raw)}
        style={{
          backgroundColor: backgroundColor || 'transparent',
        }}
      >
        {exclaim ? (
          <Exclamation />
        ) : (
          subjects.map((subject, i) => (
            <AvatarImage
              bordered={bordered}
              greyscale={greyscale}
              key={i}
              {...subject}
            />
          ))
        )}
      </span>

      {match(badge, {
        [Badge.None]: () => <></>,
        [Badge.Online]: () => (
          <span className={styles.presence}>
            <PresenceIndicator present={true} />
          </span>
        ),
        [Badge.Offline]: () => (
          <span className={styles.presence}>
            <PresenceIndicator present={false} />
          </span>
        ),
        [Badge.CheckMark]: () => (
          <span className={cx(styles.badge, styles.checked)}>
            <Icon auto name="radio-checked" />
          </span>
        ),
        [Badge.ErrorMark]: () => (
          <span className={cx(styles.badge, styles.error)}>
            <Icon auto name="radio-error" />
          </span>
        ),
      })}
    </span>
  )
})

/**
 * The formations based on subject length.
 */
const formations: Record<number, AvatarImagePosition[]> = {
  0: ['full'],
  1: ['full'],
  2: ['left', 'right'],
  3: ['left', 'topRight', 'bottomRight'],
  4: ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'],
}

/**
 * Props for AvatarImage.
 */
interface AvatarImageProps extends AvatarSubject {
  greyscale?: boolean
  position: AvatarImagePosition
  type: 'image' | 'count'
  count?: number

  /**
   * By default, when setting `bordered` to `true`, the `size` you specify
   * will be preserved. The image itself will shrink to accommodate the border
   * in a manner that satisfies: `(avatar size) + (2 * border-size) = size`.
   */
  bordered?: boolean
}

/**
 * Individual avatar.
 */
function AvatarImage({
  greyscale,
  avatar: url,
  avatarFallback: fallback,
  displayName: title,
  position,
  type,
  count,
  bordered = false,
}: AvatarImageProps) {
  const [usingFallback, setUsingFallback] = React.useState(false)
  const onImageLoadError = React.useCallback(() => setUsingFallback(true), [])
  React.useEffect(
    () => setUsingFallback(Boolean(fallback && !url)),
    [url, fallback],
  )
  return type === 'image' ? (
    <span
      className={cx(
        styles.mask,
        styles[position],
        greyscale && styles.greyscale,
      )}
    >
      <img
        onError={onImageLoadError}
        className={cx(styles.image, bordered && styles.bordered)}
        title={title ?? undefined}
        alt={title ?? undefined}
        src={
          (usingFallback ? fallback || defaultAvatarUrl : url!) ||
          defaultAvatarUrl
        }
      />
    </span>
  ) : (
    <Count count={count!} />
  )
}

/**
 * Count badge.
 */
function Count({ count }: { count: number }) {
  const text = count > 99 ? '99+' : count.toString()
  return (
    <span className={styles.count}>
      <span
        className={cx(styles.countText, styles[`countTextSize${text.length}`])}
      >
        {text}
      </span>
    </span>
  )
}

/**
 * The position of the avatar image.
 */
type AvatarImagePosition =
  | 'full'
  | 'left'
  | 'topLeft'
  | 'bottomLeft'
  | 'right'
  | 'topRight'
  | 'bottomRight'

/**
 * Parses the subjects
 *
 * @param subject
 */
function mapSubjectsToImageProps(
  subject: AvatarSubject | AvatarSubject[] | null | undefined,
): Array<AvatarImageProps> {
  const result = (Array.isArray(subject) ? subject : [subject]).filter(isNotNil)
  if (result.length === 0) {
    return [
      {
        avatar: defaultAvatarUrl,
        position: 'full',
        type: 'image',
      },
    ]
  }
  if (result.length > 4) {
    return [
      {
        avatar: '',
        type: 'count',
        count: result.length,
        position: 'full',
      },
    ]
  }
  const formation = formations[result.length] || formations[0]
  return result
    .map((subject, i): AvatarImageProps | null => {
      if (!subject) {
        return null
      }
      return {
        avatar: subject.avatar,
        avatarFallback: subject.avatarFallback,
        displayName: subject.displayName,
        type: 'image',
        position: formation[i],
      }
    })
    .filter(isNotNil)
}

function Exclamation() {
  return (
    <span className={styles.exclaim}>
      <span className={styles.exclaimIcon}>
        <Icon auto name="alert-avatar" />
      </span>
    </span>
  )
}
